/**
 * addon-products.js
 *
 * - Reads variant→addon map from data-variant-addon-map
 * - Swaps addon checkboxes when main product variant changes
 * - Updates product title links when variant changes
 * - Tracks "learn more" clicks for future analytics
 */

(function () {
  'use strict';

  const AddonProducts = {
    wrapper: null,
    hasVariants: false,
    variantAddonMap: {},

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._setup());
      } else {
        this._setup();
      }
    },

    _setup() {
      this.wrapper = document.querySelector('[data-addon-products]');
      if (!this.wrapper) return;

      this.hasVariants = this.wrapper.dataset.hasVariants === 'true';

      if (this.hasVariants) {
        try {
          this.variantAddonMap = JSON.parse(this.wrapper.dataset.variantAddonMap || '{}');
        } catch (e) {
          console.error('[AddonProducts] Could not parse variant addon map', e);
          return;
        }

        this._watchVariantChanges();

        const initialVariantId = this._getSelectedVariantId();
        if (initialVariantId) this._updateAddons(initialVariantId);
      }

      this._bindRowClicks();
    },

    // Row click toggles checkbox; link click opens new tab (handled natively)
    _bindRowClicks() {
      this.wrapper.querySelectorAll('[data-addon-row]').forEach((row) => {
        // Make row keyboard accessible
        row.setAttribute('role', 'group');

        row.addEventListener('click', (e) => {
          // If the click was on the title link or inside it, let it open new tab naturally
          if (e.target.closest('.addon-products__title')) {
            const title = e.target.closest('[data-addon-product]')?.dataset.addonProduct
              || e.target.closest('.addon-products__title')?.dataset.addonProduct;
            if (title) this.trackClick(title);
            return;
          }

          // Otherwise toggle the checkbox
          const checkbox = row.querySelector('[data-addon-checkbox]');
          if (checkbox && !checkbox.disabled) {
            checkbox.checked = !checkbox.checked;
          }
        });
      });
    },

    // ── Variant watching ───────────────────────────────────────────────────────

    _watchVariantChanges() {
      document.addEventListener('variant:changed', (e) => {
        const variantId = e.detail?.variant?.id;
        if (variantId) this._updateAddons(variantId);
      });

      document.addEventListener('variantChange', (e) => {
        const variantId = e.detail?.variant?.id;
        if (variantId) this._updateAddons(variantId);
      });

      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.addEventListener('change', (e) => {
          const variantId = parseInt(e.target.value, 10);
          if (variantId) this._updateAddons(variantId);
        });
      }
    },

    _getSelectedVariantId() {
      const input = document.querySelector('input[name="id"]');
      return input ? parseInt(input.value, 10) : null;
    },

    // ── Update addon UI ────────────────────────────────────────────────────────

    _updateAddons(variantId) {
      const data = this.variantAddonMap[variantId];

      // If this variant has no addons at all, hide the entire block
      const hasAnyAddon = data && (data.addon_1 || data.addon_2);
      this.wrapper.style.display = hasAnyAddon ? '' : 'none';
      if (!hasAnyAddon) return;

      this._updateAddonSlot(0, data ? {
        id: data.addon_1,
        price: data.addon_1_price,
        available: data.addon_1_available,
        title: data.addon_1_title,
        url: data.addon_1_url,
        variant_title: data.addon_1_variant_title,
      } : null);

      this._updateAddonSlot(1, data ? {
        id: data.addon_2,
        price: data.addon_2_price,
        available: data.addon_2_available,
        title: data.addon_2_title,
        url: data.addon_2_url,
        variant_title: data.addon_2_variant_title,
      } : null);
    },

    _updateAddonSlot(index, addon) {
      const item     = this.wrapper.querySelector(`[data-addon-item="${index}"]`);
      const checkbox = this.wrapper.querySelector(`[data-addon-checkbox="${index}"]`);
      const priceEl  = this.wrapper.querySelector(`[data-addon-price="${index}"]`);
      const titleEl  = this.wrapper.querySelector(`[data-addon-title="${index}"]`);
      let soldOutEl  = this.wrapper.querySelector(`[data-addon-soldout="${index}"]`);

      if (!addon || !addon.id) {
        if (item) item.style.display = 'none';
        return;
      }

      if (item) item.style.display = '';

      if (checkbox) {
        checkbox.value    = addon.id;
        checkbox.disabled = !addon.available;
        if (!addon.available) checkbox.checked = false;
      }

      if (priceEl && addon.price != null) {
        priceEl.textContent = this._formatMoney(addon.price);
      }

      // Update title text and href
      const variantEl = this.wrapper.querySelector(`[data-addon-variant="${index}"]`);

      if (variantEl && addon.variant_title && addon.variant_title !== 'Default Title') {
        variantEl.textContent = addon.variant_title;
        variantEl.style.display = '';
      } else if (variantEl) {
        variantEl.style.display = 'none';
      }

      if (titleEl) {
        if (addon.title) {
          const textNode = Array.from(titleEl.childNodes).find(n => n.nodeType === Node.TEXT_NODE);
          if (textNode) {
            textNode.textContent = addon.title + ' ';
          } else {
            titleEl.prepend(document.createTextNode(addon.title + ' '));
          }
          titleEl.dataset.addonProduct = addon.title;
        }
        if (addon.url) {
          titleEl.href = addon.url + (addon.id ? '?variant=' + addon.id : '');
        }
      }

      // Sold out
      const infoEl = this.wrapper.querySelector(`.addon-products__info`);
      if (!addon.available) {
        if (!soldOutEl && item) {
          soldOutEl = document.createElement('span');
          soldOutEl.className = 'addon-products__soldout';
          soldOutEl.dataset.addonSoldout = index;
          soldOutEl.textContent = 'Sold out';
          // Append inside info span, not after price
          const infoSpan = item.querySelector('.addon-products__info');
          if (infoSpan) infoSpan.appendChild(soldOutEl);
        } else if (soldOutEl) {
          soldOutEl.style.display = '';
        }
        item?.classList.add('addon-products__item--unavailable');
      } else {
        if (soldOutEl) soldOutEl.style.display = 'none';
        item?.classList.remove('addon-products__item--unavailable');
      }
    },

    // ── Analytics ──────────────────────────────────────────────────────────────

    trackClick(productTitle) {
      // Log for now — wire up GA4 here when ready
      console.log('[AddonProducts] Info click:', productTitle);

      // Fire a custom event — ready for GTM or GA4 to pick up later
      document.dispatchEvent(new CustomEvent('addon:info_click', {
        bubbles: true,
        detail: { product: productTitle }
      }));

      // If GA4 is available (gtag)
      if (typeof gtag === 'function') {
        gtag('event', 'addon_info_click', {
          addon_product: productTitle
        });
      }

      // If Google Tag Manager dataLayer is available
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'addon_info_click',
          addon_product: productTitle
        });
      }
    },

    // ── Cart ───────────────────────────────────────────────────────────────────

    getCheckedVariantIds() {
      return Array.from(
        this.wrapper?.querySelectorAll('[data-addon-checkbox]:checked:not(:disabled)') || []
      ).map((cb) => parseInt(cb.value, 10)).filter(Boolean);
    },

    async addVariantsToCart(variantIds) {
      if (!variantIds.length) return;

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: variantIds.map((id) => ({ id, quantity: 1 })) }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Could not add add-on to cart');
      }

      return res.json();
    },

    // ── Helpers ────────────────────────────────────────────────────────────────

    _formatMoney(cents) {
      const fmt = window.Shopify?.moneyFormat || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      return fmt
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', Math.round(cents / 100))
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
    },
  };

  AddonProducts.init();
  window.AddonProducts = AddonProducts;
})();